<script setup>
/**
 * Sample:
     <bn-list-box
         title="Qualifications"
         :items="itemsArray"
         item-text="display"
         item-icon="badge"
         inactive
         add="Add qualification"
         add-icon="plus"
         :add-menu="[
             { icon: 'plus', label: 'Item 1', value: '1' },
             { icon: 'plus', label: 'Item 2', value: '2' },
         ]"
         remove
         restore
         @add="addQualificationDialog"
         @remove="removeQualification"
     />

    itemsArray items can have the following properties:
        icon: ['far', 'plus'] or 'plus'
        chip: { value: 'text', icon: 'plus', color: 'green', dark: true }
        archived: true
 */
import { ref, computed, watch } from 'vue';
import BnInfo from '@/components/BnInfo.vue';
import BnMenu from '@/components/BnMenu.vue';
import BnMenuItem from '@/components/BnMenuItem.vue';
import BnLoading from '@/components/BnLoading.vue';
import BnTooltip from '@/components/BnTooltip.vue';
import draggable from 'vuedraggable';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const emits = defineEmits(['click', 'add', 'remove', 'restore', 'link', 'info', 'reorder', 'addSubItem']);

const props = defineProps({
    //header
    title: String,
    message: String,
    info: {
        type: [Boolean, String, Object],
        default() {
            return false;
        },
    },
    error: {
        type: [String, Object],
        default() {
            return {};
        },
    },
    warning: {
        type: [String, Object],
        default() {
            return {};
        },
    },
    add: [Boolean, String, Object],
    addIcon: {
        type: [Array, String],
        default: () => 'plus',
    },
    addMenu: {
        type: [Array],
        default: undefined,
    },
    // items to display
    items: {
        type: Array,
        default: () => [],
    },
    itemIcon: [Array, String],
    itemText: [String, Object, Array],
    itemValue: String,
    itemInfo: String,
    groupBy: String,
    groupIcon: String,
    addedItems: {
        type: Array,
        default: () => [],
    },
    alwaysShowArchiveMenu: Boolean,
    archiveLabel: {
        type: String,
        default: 'archived',
    },
    // item actions
    remove: [Boolean, Function],
    restore: Boolean,
    link: Boolean,
    linkIcon: {
        type: [Array, String],
        default: () => 'link',
    },
    // properties
    dense: Boolean,
    disabled: Boolean,
    inactive: Boolean,
    hideEmpty: {
        type: Boolean,
        default: false,
    },
    clickable: {
        type: Boolean,
        default: false,
    },
    required: Boolean,
    statusChip: Boolean,
    loading: Boolean,
    usprivacy: Boolean,
    dataCy: {
        type: String,
        default: 'bnListBox',
    },
    orderable: { type: Boolean, default: false },
    subItemChip: { type: Boolean, default: false },
    subItems: { type: Array, default: () => [] },
    // Resolved HealthcareService records for any service.id referenced by `items` but not
    // present in `subItems` (typically archived or per-diem leftovers). Used purely to look
    // up the procedure name for the chip text — NOT shown in the dropdown.
    archivedSubItems: { type: Array, default: () => [] },
});

const showArchived = ref(false);
const hideEmptyList = computed(() => {
    return props.hideEmpty && !props.items.length;
});
const iconAdd = computed(() => {
    return typeof props.addIcon === 'string' ? ['far', props.addIcon] : props.addIcon;
});
const iconItem = computed(() => {
    return typeof props.itemIcon === 'string' ? ['far', props.itemIcon] : props.itemIcon;
});
const iconLink = computed(() => {
    return typeof props.linkIcon === 'string' ? ['far', props.linkIcon] : props.linkIcon;
});
const itemClickHandler = computed(() => {
    return props.clickable ? 'click' : null;
});
const showArchiveMenu = computed(() => {
    return hasArchivedItems.value || props.alwaysShowArchiveMenu;
});
const hasArchivedItems = computed(() => {
    return props.items?.some((item) => item.archived || item.discontinued);
});
const visibleItems = computed(() => {
    return showArchived.value ? props.items : props.items.filter((item) => !item.archived);
});
const groupedItems = computed(() => {
    if (props.groupBy) {
        let activeGroup = null;
        return props.items.map((item) => {
            const group = item[props.groupBy];
            if (group !== activeGroup) {
                activeGroup = group;
                item.listBoxGroup = group;
            }
            return item;
        });
    } else {
        // Spread so the computed tracks the array's length/indices (re-evaluating on in-place
        // push/splice) and returns a fresh reference (firing the dragItems watcher). Callers that
        // bind a directly-mutated ref array would otherwise never update the rendered list.
        return [...props.items];
    }
});
// Local mutable mirror of the rendered list so vuedraggable can reorder it live during a
// drag (animating the other rows out of the way). Binding :list directly to the computed
// would let Vue re-render over SortableJS, so the rows wouldn't shift until drop.
const dragItems = ref([]);
watch(groupedItems, (val) => (dragItems.value = val ? [...val] : []), { immediate: true });
const allowRemove = computed(() => {
    if (props.required) {
        return props.items.filter((item) => !item.archived).length > 1;
    } else return true;
});
const requiredErrorMessage = computed(() => {
    return props.required && !props.items.length ? 'Required item' : undefined;
});
const getItemIcon = function (item) {
    if (typeof item.icon === 'string' && (item.icon?.startsWith('$') || item.icon?.startsWith('fa-kit'))) {
        return item.icon;
    }
    if (item.icon) {
        return typeof item.icon === 'string' ? ['far', item.icon] : item.icon;
    } else return iconItem.value;
};
const getItemTitle = function (item, index, separator = ' - ') {
    // simple string
    if (typeof item === 'string') {
        return { text: item };
    }
    // all other types
    if (typeof props.itemText === 'object' && !Array.isArray(props.itemText)) {
        // Use the rendered row index (from vuedraggable) rather than props.items.indexOf(item):
        // props.items can be a computed returning fresh objects each tick, so identity lookup
        // misses and the order number falls back to 0. (#2985)
        let orderNumber = index + 1;
        const title = props?.orderable ? orderNumber + '. ' + item[props.itemText.title] : item[props.itemText.title];
        return typeof title === 'object' ? title : { text: title };
    } else if (props.itemText) {
        if (props.itemText === 'url') {
            return { text: props.getDisplay(item?.url) };
        }
        if (Array.isArray(props.itemText) && props.itemText.length >= 2) {
            let stringFromArray = '';
            props.itemText.forEach((e, index) => {
                if (item[e.toString()]) {
                    stringFromArray += item[e.toString()];
                    if (index !== props.itemText.length - 1) {
                        stringFromArray += separator;
                    }
                }
            });
            return { text: stringFromArray };
        }
        return { text: item[props.itemText] };
    } else {
        return { text: item?.display || item?.text || item?.name };
    }
};
const getItemSubTitle = function (item) {
    const subtitle = item[props.itemText?.subtitle];
    return typeof subtitle === 'object' ? subtitle : { text: subtitle };
};
const getItemValue = function (item) {
    return props.itemValue ? item[props.itemValue.toString()] : item;
};
const getItemChip = function (item) {
    if (Array.isArray(item.chip)) return item.chip;
    else return item.chip ? [item.chip] : [];
};
const getServiceCode = (element) => element?.service && props.subItems?.find((item) => (element.service.id ? item.id === element.service.id : item.name === element.service.display))?.billingCode;
// For archived/per-diem procedures that aren't in `subItems`, look up the
// billingCode from `archivedSubItems` so the chip is consistent with the
// active state (which also shows a billing code via getServiceCode).
// Falls back to the saved service.display only if nothing else is found.
const getArchivedBillingCode = (element) => {
    const serviceId = element?.service?.id;
    if (!serviceId) return element?.service?.display;
    const match = props.archivedSubItems?.find((item) => item.id === serviceId);
    return match?.billingCode || element?.service?.display;
};
// Returns true when `item` (a sub-item menu option) is the currently-selected service on `element`.
// Used to highlight the active option in the sub-item dropdown.
const isSelectedSubItem = (item, element) => {
    if (!element?.service) return false;
    if (element.service.display === 'non-billable') return item.name === 'Non-billable';
    if (element.service.id && item.id) return element.service.id === item.id;
    if (element.service.display && item.name) return element.service.display === item.name;
    return false;
};
const addItem = function (menuItem) {
    emits('add', menuItem);
};
const removeItem = function (item, event) {
    event.preventDefault();
    event.stopPropagation();
    emits('remove', getItemValue(item));
};
const restoreItem = function (item) {
    emits('restore', getItemValue(item));
};
const openLink = function (item) {
    emits('link', getItemValue(item));
};
const clickItem = function (item) {
    emits('click', getItemValue(item));
};
const clickInfo = function () {
    emits('info');
};
const setOrder = function () {
    // vuedraggable has already reordered dragItems in place during the drag.
    emits('reorder', [...dragItems.value]);
};
defineExpose({ showArchived });
</script>

<template>
    <v-card v-if="!hideEmptyList" :border="false" :data-cy="dataCy" class="no-select" :class="{ 'overflow-visible': requiredErrorMessage }">
        <div class="d-flex">
            <div v-if="title" class="bn-section-header align-self-center" :class="{ 'bnList-required-field': requiredErrorMessage }" v-html="title"></div>
            <v-spacer></v-spacer>
            <!-- message text -->
            <div class="text-caption align-self-center ml-4">{{ message }}</div>
            <div class="ml-4 d-flex">
                <!-- ERROR icon/button -->
                <template v-if="error.message || requiredErrorMessage">
                    <BnInfo type="dialog" :header="error.header || 'Attention'" :icon="error.icon || 'exclamation-circle'" :color="error.color || 'error'" class="ml-2 mt-2" size="lg">
                        {{ error.message || requiredErrorMessage }}
                    </BnInfo>
                </template>
                <!-- WARNING icon/button -->
                <template v-if="warning.message">
                    <BnInfo type="dialog" :header="warning.header || 'Attention'" :icon="warning.icon || 'exclamation-circle'" :color="warning.color || 'warning'" class="ml-2" size="lg">
                        {{ warning.message }}
                    </BnInfo>
                </template>
                <!-- INFO icon/button -->
                <template v-if="info && !(error.message || requiredErrorMessage)">
                    <BnInfo v-if="typeof info === 'string'" type="dialog" class="ml-2" :class="{ 'mr-2': showArchiveMenu }" :message="info" />
                    <BnInfo
                        v-else-if="typeof info === 'object'"
                        :type="info.type || 'dialog'"
                        :header="info.header || 'Info'"
                        :icon="info.icon || 'info-circle'"
                        :color="info.color || 'info'"
                        class="ml-2 align-self-center"
                        :class="{ 'mr-2': showArchiveMenu }"
                        :message="info.message"
                    />
                    <v-btn v-else variant="text" icon color="info" class="ml-2" :class="{ 'mr-2': showArchiveMenu }" @click="clickInfo">
                        <FontAwesomeIcon :icon="['far', 'info-circle']" size="lg" />
                    </v-btn>
                </template>
                <!-- archive ellipsis menu -->
                <BnMenu v-if="showArchiveMenu" offset-y left icon="ellipsis-v" class="ml-2" :data-cy="dataCy + 'ArchiveMenu'">
                    <BnMenuItem v-if="!showArchived" icon="eye" :label="'Show ' + archiveLabel" :disabled="!hasArchivedItems" :data-cy="dataCy + 'ArchiveMenuShow'" @click="showArchived = true" />
                    <BnMenuItem
                        v-if="showArchived"
                        icon="eye-slash"
                        :label="'Hide ' + archiveLabel"
                        :disabled="!hasArchivedItems"
                        :data-cy="dataCy + 'ArchiveMenuHide'"
                        @click="showArchived = false"
                    />
                </BnMenu>
                <!-- Add item to list -->
                <template v-if="add">
                    <!-- addMenu -->
                    <BnMenu v-if="addMenu" :disabled="disabled || add === 'disabled'" color="primary" :icon="iconAdd" class="ml-2 addBtn" :data-cy="dataCy + 'AddMenu'">
                        <template v-for="(menuItem, index) in addMenu" :key="menuItem + index">
                            <v-divider v-if="menuItem.divider" />
                            <BnMenuItem
                                :icon="menuItem.icon"
                                :label="menuItem.label"
                                :disabled="menuItem.disabled"
                                :data-cy="dataCy + 'AddMenuItem'"
                                @click="addItem(menuItem.value || menuItem.label)"
                            />
                        </template>
                    </BnMenu>
                    <!-- Regular add button -->
                    <v-btn v-else :disabled="disabled || add === 'disabled'" variant="text" icon color="primary" class="ml-2 addBtn" :data-cy="dataCy + 'AddItemButton'" @click="addItem">
                        <FontAwesomeIcon :icon="iconAdd" size="lg" />
                    </v-btn>
                </template>
            </div>
        </div>
        <v-card class="mb-4" :class="{ usprivacy: usprivacy }">
            <!-- loading -->
            <BnLoading v-if="loading" type="card" class="pa-4" />
            <!-- Use slot -->
            <!-- Build the list -->
            <slot v-else>
                <v-card v-if="!visibleItems.length" :disabled="disabled" :border="false" class="d-flex text-caption pa-2">
                    <div v-if="add" class="ml-auto">
                        {{ add }}
                        <FontAwesomeIcon :icon="['far', 'level-up']" class="mr-3" />
                    </div>
                    <div v-else>None</div>
                </v-card>
                <draggable ghost-class="bnListBox-drag-ghost" :animation="150" handle=".draggable-handle" item-key="title" :list="dragItems" @end="setOrder">
                    <template #item="{ element, index }">
                        <div>
                            <!-- GroupBy header -->
                            <template v-if="element.listBoxGroup">
                                <v-divider v-if="index"></v-divider>
                                <v-list-item :key="'group' + index" density="compact" class="bn-color-list-group font-weight-medium">
                                    <FontAwesomeIcon v-if="groupIcon" :icon="['far', groupIcon]" class="mr-2" />
                                    {{ element?.listBoxGroup }}
                                </v-list-item>
                                <v-divider v-if="!index" :key="'groupdiv' + index"></v-divider>
                            </template>
                            <!-- List items -->
                            <template v-if="!element.archived || showArchived">
                                <v-divider v-if="(showArchived || dragItems.filter((gi) => !gi.archived).length > 1) && index" :key="'div' + index"></v-divider>
                                <v-list-item
                                    :key="'li' + index"
                                    :disabled="disabled || element.disabled || addedItems.some((i) => i[itemValue] === element[itemValue])"
                                    :inactive="inactive"
                                    class="pr-0"
                                    :href="element.href"
                                    :target="element.href ? '_blank' : undefined"
                                    :data-cy="dataCy + 'Item'"
                                    @[itemClickHandler]="clickItem(element)"
                                >
                                    <template v-if="!dense" #prepend>
                                        <div v-if="getItemIcon(element)" class="mr-4 align-self-center" :data-cy="dataCy + 'ItemIcon'">
                                            <v-icon v-if="typeof getItemIcon(element) === 'string' && getItemIcon(element)?.startsWith('$')" :icon="getItemIcon(element)" size="x-small"></v-icon>
                                            <i v-else-if="typeof getItemIcon(element) === 'string' && getItemIcon(element)?.startsWith('fa-kit')" :class="getItemIcon(element)" class="mr-n1"></i>
                                            <FontAwesomeIcon v-else :icon="getItemIcon(element)" />
                                        </div>
                                        <!-- Chip -->
                                        <div v-if="getItemChip(element).length" class="d-flex flex-column">
                                            <div v-for="(chip, index) of getItemChip(element)" :key="element.id + 'chip' + index" style="margin: 1px" class="d-flex align-content-right">
                                                <v-spacer></v-spacer>
                                                <v-chip
                                                    size="x-small"
                                                    :theme="chip.dark ? 'dark' : 'light'"
                                                    :label="chip.label"
                                                    :variant="chip.outlined ? 'outlined' : 'flat'"
                                                    :color="chip.color"
                                                    class="px-2 mr-2 font-weight-medium"
                                                >
                                                    <FontAwesomeIcon v-if="chip.icon && !$vuetify.display.smAndDown" :icon="['far', chip.icon]" class="mr-1" />
                                                    <span v-text="chip.value"></span>
                                                </v-chip>
                                            </div>
                                        </div>
                                    </template>
                                    <v-list-item-title>
                                        <FontAwesomeIcon v-if="getItemTitle(element, index).icon" :icon="['far', getItemTitle(element, index).icon]" class="mr-2" />
                                        <span :class="{ 'line-wrap': element.isHTML }">{{ getItemTitle(element, index).text }}</span>
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        <FontAwesomeIcon
                                            v-if="getItemSubTitle(element).icon"
                                            :icon="['far', getItemSubTitle(element).icon]"
                                            class="mr-2"
                                            :class="{ 'ml-6': getItemTitle(element, index).icon }"
                                        />
                                        <span :class="{ 'line-wrap': element.isHTML }">{{ getItemSubTitle(element).text }}</span>
                                    </v-list-item-subtitle>
                                    <template #append>
                                        <template v-if="subItemChip">
                                            <v-chip
                                                v-if="!element.active"
                                                size="x-small"
                                                variant="outlined"
                                                :color="element.service && element.service.display !== 'non-billable' && !getServiceCode(element) ? 'warning' : 'primary'"
                                                class="mr-2 px-2"
                                                data-cy="subItemChip"
                                            >
                                                <div class="d-inline-flex align-center">
                                                    <div v-if="element.service?.display === 'non-billable'" class="align-self-center">Non-billable</div>
                                                    <div v-else-if="getServiceCode(element)" class="align-self-center">
                                                        {{ getServiceCode(element) }}
                                                    </div>
                                                    <!-- Previously selected procedure no longer in subItems (e.g. archived or per-diem) -->
                                                    <BnTooltip v-else-if="element.service" bottom :icon="false" message="This code is archived">
                                                        <div class="align-self-center d-inline-flex align-center" data-cy="subItemChipArchived">
                                                            <FontAwesomeIcon v-if="!$vuetify.display.smAndDown" :icon="['far', 'exclamation-triangle']" class="mr-1" />
                                                            <span>{{ getArchivedBillingCode(element) }}</span>
                                                        </div>
                                                    </BnTooltip>
                                                    <FontAwesomeIcon class="ml-2 align-self-center" :icon="['far', 'angle-down']" />
                                                </div>
                                                <v-menu activator="parent">
                                                    <v-list style="max-height: 400px" class="scroll-on">
                                                        <v-list-item
                                                            v-for="(item, index) in subItems"
                                                            :key="index"
                                                            :value="index"
                                                            :active="isSelectedSubItem(item, element)"
                                                            @click="emits('addSubItem', { subItem: item, form: element })"
                                                        >
                                                            <template v-if="isSelectedSubItem(item, element)" #prepend>
                                                                <FontAwesomeIcon :icon="['far', 'check']" class="mr-2 text-primary" />
                                                            </template>
                                                            <v-list-item-title :data-cy="'subItem-' + item.name" :data-cy-selected="isSelectedSubItem(item, element) ? 'true' : undefined">
                                                                <span v-if="item.name === 'Non-billable'">{{ item.name }}</span>
                                                                <span v-if="item.billingCode">({{ item.billingCode }}) </span
                                                                ><span v-if="item.name && item.name !== 'Non-billable'"> - {{ item.name }}</span>
                                                            </v-list-item-title>
                                                        </v-list-item>
                                                    </v-list>
                                                </v-menu>
                                            </v-chip>
                                        </template>
                                        <!-- Status Chip -->
                                        <template v-if="statusChip">
                                            <v-chip v-if="!element.active" size="x-small" color="warning" class="mr-2 px-2">
                                                <FontAwesomeIcon v-if="!$vuetify.display.smAndDown" :icon="['far', 'archive']" class="mr-1" />
                                                <span>Archived</span>
                                            </v-chip>
                                            <v-chip v-else size="x-small" color="success" class="mr-2 px-2">
                                                <FontAwesomeIcon v-if="!$vuetify.display.smAndDown" :icon="['fad', 'spinner-third']" class="mr-1" />
                                                <span>Active</span>
                                            </v-chip>
                                        </template>
                                        <!-- Info -->
                                        <template v-if="element[itemInfo]">
                                            <BnInfo type="dialog">{{ element[itemInfo] }}</BnInfo>
                                        </template>
                                        <!-- Restore -->
                                        <template v-if="restore && (element.archived || element.inactive || element.active === false)">
                                            <v-chip size="x-small" color="warning">
                                                <FontAwesomeIcon v-if="!$vuetify.display.smAndDown" :icon="['far', 'archive']" class="mr-1" />
                                                <span>{{ archiveLabel }}</span>
                                            </v-chip>
                                            <template v-if="!disabled">
                                                <v-btn variant="text" icon color="green" :data-cy="dataCy + 'RestoreItem'" @click.stop="restoreItem(element)">
                                                    <FontAwesomeIcon :icon="['far', 'redo']" size="lg" />
                                                </v-btn>
                                            </template>
                                        </template>
                                        <!-- Remove -->
                                        <template v-else-if="typeof remove === 'function' ? remove(element) : remove && allowRemove && !disabled">
                                            <v-btn variant="text" icon color="red" :data-cy="dataCy + 'RemoveItem'" @click.stop="removeItem(element, $event)">
                                                <FontAwesomeIcon :icon="['far', 'minus-circle']" />
                                            </v-btn>
                                        </template>
                                        <!-- Export/Open Link -->
                                        <template v-else-if="link && !disabled">
                                            <v-btn variant="text" icon color="success" @click.stop="openLink(element)">
                                                <FontAwesomeIcon :icon="iconLink" size="lg" />
                                            </v-btn>
                                        </template>
                                        <template v-if="orderable">
                                            <v-btn variant="text" icon size="small" class="draggable-handle">
                                                <FontAwesomeIcon :icon="['far', 'grip-lines']" size="lg" />
                                            </v-btn>
                                        </template>
                                    </template>
                                </v-list-item>
                            </template>
                        </div>
                    </template>
                </draggable>
            </slot>
        </v-card>
    </v-card>
</template>

<style>
/* Drop placeholder while dragging: hide the moved row but keep its box, leaving an empty
   gap the other (animated) rows shift around to show where it will land. */
.bnListBox-drag-ghost {
    visibility: hidden;
}

.address-missing {
    color: #e53935;
    font-weight: bold;
    letter-spacing: 1px;
    margin-left: 8px;
}
</style>
